import TagBadge from "@/ui/links/tag-badge";
import { CardList, Checkbox, ArrowTurnRight2, Button, Tooltip } from "@panda0/panda0-ui";
import { ArrowRight, Star, Plus, Eye } from "lucide-react";
import React, { useContext, useState } from "react";
import { CandidateListContext, InsightsDisplayContext } from "./types";
import { getRankEmoji } from "./get-rank-emoji";

// Candidate Card Component (for cards view)
const CandidateCard = ({ candidate, compact, openCandidateModal }) => {
  const { selectedCandidates, setSelectedCandidates } =
    useContext(CandidateListContext);
  const { displayProperties } = useContext(InsightsDisplayContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSelectCandidate = () => {
    const newSelectedCandidates = new Set(selectedCandidates);
    if (newSelectedCandidates.has(candidate.id)) {
      newSelectedCandidates.delete(candidate.id);
    } else {
      newSelectedCandidates.add(candidate.id);
    }
    setSelectedCandidates(newSelectedCandidates);
  };

  const score = parseInt(candidate.score, 10);
  let scoreBgColor = "bg-[#ffe2dd66]";
  if (score >= 90) scoreBgColor = "bg-[#aae3bf66]";
  else if (score >= 70) scoreBgColor = "bg-[#fdecc866]";

  return (
    <CardList.Card innerClassName="flex flex-col p-4 h-full">
      <div className="flex items-center w-full gap-x-6 py-3">
        {/* Checkbox */}
        <div className="w-[40px] flex items-center">
          <Checkbox
            checked={selectedCandidates.has(candidate.id)}
            onCheckedChange={handleSelectCandidate}
            aria-label={`Select candidate ${candidate.id}`}
          />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-6">
            <div className="min-w-0 shrink grow-0 text-neutral-950">
              {displayProperties.includes("name") && (
                <div className="w-[180px]">
                  <span className="font-medium block">{candidate.name}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row gap-4 items-center mt-2.5">
            {/* Rank */}
            {displayProperties.includes("rank") &&
              (compact ? (
                <ArrowRight className="mr-1 h-3 w-3 shrink-0 text-neutral-400" />
              ) : (
                <ArrowTurnRight2 className="h-3 w-3 shrink-0 text-neutral-400" />
              ))}
            <div className="flex items-center w-[10%]">
              <span className="inline-flex justify-center items-center w-[25px] h-[20px] rounded-full text-xs font-medium bg-green-500 text-white">
                {candidate.rank}
              </span>
              <span>{getRankEmoji(candidate.rank)}</span>
            </div>

            {/* Job Fit */}
            <Tooltip
              content={
                <div className="w-full px-4 py-2 flex flex-col">
                  <div className="flex flex-col gap-1.5">
                    <p className="text-sm font-semibold text-neutral-700">
                      {`${candidate.jobFit}%`}
                    </p>
                    <p className="flex flex-col gap-1 text-xs text-neutral-500">
                      Jobfit
                    </p>
                  </div>
                </div>
              }
            >
              {displayProperties.includes("jobFit") && (
                <div className="w-[100px] flex items-center gap-2">
                  <svg className="w-[25%]" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#E6E6E6"
                      strokeWidth="4"
                    />
                    <path
                      d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#15a349"
                      strokeWidth="4"
                      strokeDasharray={`${(parseInt(candidate.jobFit))}, 100`}
                    />
                  </svg>
                  <p className="text-green-600">{`${candidate.jobFit}%`}</p>
                </div>
              )}
            </Tooltip>

            {/* Score */}
            <Tooltip
              content={
                <div className="w-full px-4 py-2 flex flex-col">
                  <div className="flex flex-col gap-1.5">
                    <p className="text-sm font-semibold text-neutral-700">
                      {`${candidate.score}`}
                    </p>
                    <p className="flex flex-col gap-1 text-xs text-neutral-500">
                      Score
                    </p>
                  </div>
                </div>
              }
            >
              {displayProperties.includes("score") && (
                <div className="w-[100px]">
                  <TagBadge
                    key={candidate.name}
                    name={`${candidate.score}`}
                    color={
                      score >= 90 ? "green" : score >= 70 ? "yellow" : "red"
                    }
                    className={`animate-fade-in text-center ${score >= 90 ? "bg-green-100 border border-green-300 text-green-600" : score >= 70 ? "bg-yellow-100 border border-yellow-300 text-yellow-600" : "bg-red-100 border border-red-300 text-red-600"}`}
                  />
                </div>
              )}
            </Tooltip>

            {/* Source */}
            <Tooltip
              content={
                <div className="w-full px-4 py-2 flex flex-col">
                  <div className="flex flex-col gap-1.5">
                    <p className="text-sm font-semibold text-neutral-700">
                      {candidate.source}
                    </p>
                    <p className="flex flex-col gap-1 text-xs text-neutral-500">
                      Source
                    </p>
                  </div>
                </div>
              }
            >
              {displayProperties.includes("source") && (
                <div className="w-[100px]">
                  <span className="block text-center">{candidate.source}</span>
                </div>
              )}
            </Tooltip>

            {/* Date */}
            <Tooltip
              content={
                <div className="w-full px-4 py-2 flex flex-col">
                  <div className="flex flex-col gap-1.5">
                    <p className="text-sm font-semibold text-neutral-700">
                      {candidate.date}
                    </p>
                    <p className="flex flex-col gap-1 text-xs text-neutral-500">
                      Date
                    </p>
                  </div>
                </div>
              }
            >
              {displayProperties.includes("date") && (
                <div className="w-[140px]">
                  <span className="block">{candidate.date}</span>
                </div>
              )}
            </Tooltip>

            {/* Response Time */}
            <Tooltip
              content={
                <div className="w-full px-4 py-2 flex flex-col">
                  <div className="flex flex-col gap-1.5">
                    <p className="text-sm font-semibold text-neutral-700">
                      {candidate.responseTime}
                    </p>
                    <p className="flex flex-col gap-1 text-xs text-neutral-500">
                      Response Time
                    </p>
                  </div>
                </div>
              }
            >
              {displayProperties.includes("responseTime") && (
                <div className="w-[140px]">
                  <span className="block">{candidate.responseTime}</span>
                </div>
              )}
            </Tooltip>

            {/* Ratings */}
            {displayProperties.includes("ratings") && (
              <div className="w-[120px] flex items-center gap-1">
                <Star className="h-4 w-4 text-yellow-500" />
                <span className="font-medium">{candidate.ratings}</span>
              </div>
            )}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <Button
            variant="outline"
            className="text-sm h-8 px-3"
            text="Shortlist"
            icon={<Plus className="h-3 w-3" />}
          />

          {/* View Button */}
          <Button
            variant="primary"
            text="View"
            className="text-sm h-8 px-3"
            onClick={() => openCandidateModal(candidate.id)}
            icon={<Eye className="h-4 w-4" />}
          />
        </div>
      </div>
    </CardList.Card>
  );
};

export default React.memo(CandidateCard);
